import { useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Paintbrush, Check } from "lucide-react";

/**
 * Partner brand-colour picker — lets a partner override the Aii brand
 * green throughout their portal chrome. The chosen hex is stored on
 * `partner_profile.brand_color` and applied as CSS variables on the
 * portal root so every `.bg-brand` / `.text-brand` / `.border-brand`
 * tailwind utility switches without a component-level change.
 *
 * Empty string reverts to the default green.
 */
const PRESETS = [
  { name: "Aii Green", hex: "#1B7A3E" },
  { name: "Royal Blue", hex: "#1F4EAF" },
  { name: "Sunset Coral", hex: "#E15D3E" },
  { name: "Deep Purple", hex: "#5B3F9A" },
  { name: "Slate", hex: "#334155" },
  { name: "Forest", hex: "#0E5C3B" },
  { name: "Rose", hex: "#B93B6A" },
  { name: "Amber", hex: "#B96F1F" },
];

export default function BrandColorPicker({ me, onUpdated }) {
  const current = me?.partner_profile?.brand_color || "";
  const [hex, setHex] = useState(current);
  const [busy, setBusy] = useState(false);

  const save = async (value) => {
    setBusy(true);
    try {
      const res = await api.patch("/partner-portal/brand-color", { brand_color: value || "" });
      toast.success(value ? "Brand colour saved" : "Reverted to default");
      setHex(res.data.brand_color || "");
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-[color:var(--aii-border)]" data-testid="brand-color-picker">
      <div className="text-[10px] uppercase tracking-widest text-brand font-bold flex items-center gap-1 mb-1">
        <Paintbrush size={11}/> Portal accent colour
      </div>
      <p className="text-xs text-[color:var(--aii-muted)] mb-2">
        Pick an accent — it replaces the default green across your portal chrome. Leave blank for the standard theme.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {PRESETS.map((p) => (
          <button
            key={p.hex}
            type="button"
            disabled={busy}
            data-testid={`brand-swatch-${p.hex.slice(1).toLowerCase()}`}
            onClick={() => save(p.hex)}
            title={p.name}
            className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition ${hex.toLowerCase() === p.hex.toLowerCase() ? "border-slate-800 scale-110" : "border-white shadow-sm"}`}
            style={{ backgroundColor: p.hex }}>
            {hex.toLowerCase() === p.hex.toLowerCase() && <Check size={14} className="text-white drop-shadow"/>}
          </button>
        ))}
        <label className="inline-flex items-center gap-1.5 text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded-md hover:bg-[color:var(--aii-green-tint)] cursor-pointer" title="Custom hex">
          <input
            type="color"
            className="w-6 h-6 rounded border-0 cursor-pointer p-0"
            value={hex || "#1B7A3E"}
            disabled={busy}
            onChange={(e) => save(e.target.value)}
            data-testid="brand-color-custom"/>
          Custom
        </label>
        {hex && (
          <button
            type="button"
            disabled={busy}
            data-testid="brand-color-reset"
            onClick={() => save("")}
            className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50 text-[color:var(--aii-muted)]">
            Reset
          </button>
        )}
      </div>
    </div>
  );
}
